/**
 * Product Page JavaScript
 * The Color — thecolor.uk
 *
 * Handles:
 * - Variant selection and price/availability updates
 * - Image gallery and thumbnail switching
 * - Image zoom modal
 * - Quantity controls (with max bound)
 * - Add to cart via centralised TheColor.Cart API
 */

(function () {
  'use strict';

  const MAX_QUANTITY = 99;

  /**
   * Build a Shopify CDN URL with a specific width parameter.
   * Handles both legacy _600x suffix URLs and modern ?width= URLs.
   */
  function getSizedImageUrl(src, width) {
    if (!src) return '';
    // Strip legacy _NNNx or _NNNxNNN size suffixes
    var url = src.replace(/_\d+x(\d+)?(?=\.|$)/g, '');
    try {
      var parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', width);
      return parsed.pathname + parsed.search;
    } catch (e) {
      url = url.replace(/[?&]width=\d+/g, '');
      var sep = url.indexOf('?') !== -1 ? '&' : '?';
      return url + sep + 'width=' + width;
    }
  }

  // Resolve a variant's image: prefer variant.featured_image, fall back to alt/filename match
  // against productData.images so size variants without an admin-assigned image still swap.
  function findVariantImage(variant, productData) {
    if (!variant) return null;
    if (variant.featured_image) return variant.featured_image;
    if (!productData || !productData.images || !productData.images.length) return null;

    var sizeKey = variant.option1 ? variant.option1.toLowerCase().replace(/\s+/g, '') : '';
    if (!sizeKey) return null;

    var sizeNumber = parseInt(sizeKey.replace(/[^0-9]/g, ''), 10);

    // 1) Alt-text substring match (e.g., alt="Freeze and Shine 500ml")
    var altMatch = productData.images.find(function (img) {
      if (!img.alt) return false;
      return img.alt.toLowerCase().replace(/\s+/g, '').indexOf(sizeKey) !== -1;
    });
    if (altMatch) return altMatch;

    // 2) Filename numeric-group match — pulls digits out of the file portion of the URL
    //    and matches numerically (so "075" matches 75ml, "500" matches 500ml).
    if (sizeNumber && !isNaN(sizeNumber)) {
      var numericMatch = productData.images.find(function (img) {
        if (!img.src) return false;
        var fileName = img.src.split('?')[0].split('/').pop() || '';
        var groups = (fileName.match(/\d+/g) || []).map(function (g) { return parseInt(g, 10); });
        return groups.indexOf(sizeNumber) !== -1;
      });
      if (numericMatch) return numericMatch;

      // 3) Special-case: 1000ml products are sometimes filed under the legacy "999" SKU code.
      if (sizeNumber === 1000) {
        var legacyMatch = productData.images.find(function (img) {
          if (!img.src) return false;
          var fileName = img.src.split('?')[0].split('/').pop() || '';
          return /(^|[^0-9])999([^0-9]|$)/.test(fileName);
        });
        if (legacyMatch) return legacyMatch;
      }
    }

    return null;
  }

  const section = document.querySelector('[data-section-type="product-main"]');
  if (!section) return;

  // Parse product data
  const productJsonEl = section.querySelector('[data-product-json]');
  if (!productJsonEl) return;

  let product;
  try {
    product = JSON.parse(productJsonEl.textContent);
  } catch (e) {
    // Silent — bail without disrupting the page
    return;
  }

  // --- Enhanced E-Commerce: view_item event for Google Ads ---
  // Wrapped + re-firing on tc:consent-granted so we don't lose the signal
  // for visitors who land on a PDP, then accept consent a few seconds
  // later. Without the re-fire, the FIRST PDP in their journey never
  // reports view_item, which is the most important signal for Smart Bidding.
  // Resolve the variant the shopper is ACTUALLY looking at, not just the
  // first available one: a ?variant= deep link (ad clicks land here) and the
  // SSR'd hidden variant-id input (which carries the FS landing-variant
  // override) both beat the first-available fallback. Without this,
  // view_item reported the wrong variant for every ad deep-link.
  const currentVariant = (function () {
    if (!product.variants || !product.variants.length) return null;
    var urlId = null;
    try {
      urlId = new URLSearchParams(window.location.search).get('variant');
    } catch (e) { /* ignore — fall through to the SSR input */ }
    var ssrInput = section.querySelector('[data-product-variant-id]');
    /* Try each candidate until one MATCHES: a stale ?variant= id (deleted and
       recreated variants happen) must fall through to the SSR input — which
       carries the FS landing override — not straight to first-available. */
    var candidates = [urlId, ssrInput && ssrInput.value];
    for (var ci = 0; ci < candidates.length; ci++) {
      if (!candidates[ci]) continue;
      var match = product.variants.find(function (v) { return String(v.id) === String(candidates[ci]); });
      if (match) return match;
    }
    return product.variants.find(function (v) { return v.available; }) || product.variants[0];
  })();
  let viewItemFired = false;
  function fireViewItemEvent() {
    if (viewItemFired) return;
    if (typeof gtag !== 'function' || !currentVariant || !window.TheColor.utils.hasConsent()) return;
    viewItemFired = true;
    gtag('event', 'view_item', {
      currency: 'GBP',
      value: currentVariant.price / 100,
      items: [{
        /* Feed-aligned id: the Google & YouTube channel submits feed items as
           shopify_ZZ_{product}_{variant} (observed from its own remarketing
           events) — matching it lets GA4/Ads join items to the Shopping feed. */
        item_id: 'shopify_ZZ_' + product.id + '_' + currentVariant.id,
        item_name: product.title,
        item_brand: product.vendor,
        item_category: product.type || undefined,
        item_variant: currentVariant.title !== 'Default Title' ? currentVariant.title : undefined,
        google_business_vertical: 'retail',
        price: currentVariant.price / 100,
        quantity: 1
      }]
    });
  }
  fireViewItemEvent();
  // If consent wasn't granted at page load, fire once it is granted later.
  document.addEventListener('tc:consent-granted', fireViewItemEvent, { once: true });

  // Elements
  const form = section.querySelector('[data-product-form]');
  const variantIdInput = section.querySelector('[data-product-variant-id]');
  const priceEl = section.querySelector('[data-product-price]');
  const comparePriceEl = section.querySelector('[data-product-compare-price]');
  const saveBadgeEl = section.querySelector('[data-product-save-badge]');
  const addBtn = section.querySelector('[data-product-add-btn]');
  const mainImage = section.querySelector('[data-product-featured-image]');
  const thumbnails = section.querySelectorAll('[data-product-thumbnail]');
  const optionSelects = section.querySelectorAll('[data-product-option-select]');
  const optionInputs = section.querySelectorAll('[data-product-option-input]');
  const qtyInput = section.querySelector('[data-product-quantity]');
  const qtyMinus = section.querySelector('[data-product-qty-minus]');
  const qtyPlus = section.querySelector('[data-product-qty-plus]');
  const titleEl = section.querySelector('.tc-product-main__title');

  // Captured at init so the size suffix can be re-appended on variant change
  // without doubling up. Matches the Liquid-side dynamic H1 logic.
  const titleBaseText = titleEl ? (function () {
    // Strip any trailing " 75 ml" / " 1000 ml" / " 250 ml" etc.
    return (titleEl.textContent || '').replace(/\s+\d+\s*ml\s*$/i, '').trim();
  })() : '';
  const sizeOptionIndex = (function () {
    var groups = section.querySelectorAll('[data-product-option]');
    for (var i = 0; i < groups.length; i++) {
      var label = groups[i].querySelector('.tc-product-main__option-label');
      if (label && /size/i.test(label.textContent || '')) return i;
    }
    return -1;
  })();

  /**
   * Shared gallery state — ONE image index, synced across thumbnail clicks,
   * variant changes AND mobile swipes (each used to track/apply the image
   * independently; the swipe path changed an index but never updated the
   * WebP <picture> <source>, so the visible image never changed).
   *
   * NOTE: product.images in `{{ product | json }}` is an array of URL
   * STRINGS (no .src/.alt/.id), so the SSR'd thumbnail buttons — which carry
   * prebuilt url/srcset/alt/id per image, in product.images order — are the
   * source of truth for image switching.
   */
  let currentImageIndex = (function () {
    for (let i = 0; i < thumbnails.length; i++) {
      if (thumbnails[i].classList.contains('is-active')) return i;
    }
    return 0;
  })();

  // Apply src + srcset to the main <img> AND the WebP <source> in its
  // <picture> wrapper, deriving the webp srcset from the jpeg one.
  function applySrcsetToMain(src, srcset, alt) {
    if (!mainImage) return;
    mainImage.src = src;
    mainImage.srcset = srcset;
    mainImage.alt = alt;
    var pic = mainImage.closest('picture');
    if (pic) {
      var webpSource = pic.querySelector('source[type="image/webp"]');
      if (webpSource) {
        webpSource.srcset = srcset.split(',').map(function (entry) {
          var parts = entry.trim().split(/\s+/);
          var url = parts[0];
          var descriptor = parts[1] || '';
          if (url.indexOf('format=webp') === -1) {
            url += (url.indexOf('?') !== -1 ? '&' : '?') + 'format=webp';
          }
          return (url + ' ' + descriptor).trim();
        }).join(', ');
      }
    }
  }

  // Switch the gallery to product image `index` (thumbnail order = image
  // order) — updates main <img>, WebP <source>, thumbnail highlight and the
  // shared currentImageIndex in one place.
  function setMainImage(index) {
    if (!mainImage) return;
    var thumb = thumbnails[index];
    if (!thumb) return;
    currentImageIndex = index;
    applySrcsetToMain(thumb.dataset.imageUrl, thumb.dataset.imageSrcset, thumb.dataset.imageAlt);
    thumbnails.forEach(function (t, i) {
      t.classList.toggle('is-active', i === index);
      t.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }

  // Zoom
  const zoomTrigger = section.querySelector('[data-product-zoom]');
  const zoomModal = section.querySelector('[data-product-zoom-modal]');
  const zoomImage = section.querySelector('[data-product-zoom-image]');
  const zoomClose = section.querySelector('[data-product-zoom-close]');

  // Duo component availability — for duo products, check if shampoo+conditioner are in stock
  let duoComponents = null;
  const duoJsonEl = section.querySelector('[data-duo-components]');
  if (duoJsonEl) {
    try {
      duoComponents = JSON.parse(duoJsonEl.textContent);
    } catch (e) {
      // Ignore parse errors
    }
  }

  /**
   * For duo products, check if both component variants (matching by size) are available.
   * Returns false if either component's matching variant is out of stock.
   */
  function normalizeSize(s) {
    return s ? s.toLowerCase().replace(/\s+/g, '') : '';
  }

  function isDuoVariantAvailable(variant) {
    if (!duoComponents || !variant) return variant ? variant.available : false;

    var size = normalizeSize(variant.option1); // e.g. "300ml"
    var sh = duoComponents.shampoo;
    var cd = duoComponents.conditioner;

    if (!sh || !cd) return variant.available;

    var shVariant = sh.variants.find(function (v) { return normalizeSize(v.option1) === size; });
    var cdVariant = cd.variants.find(function (v) { return normalizeSize(v.option1) === size; });

    // Both component variants must exist and be available
    if (!shVariant || !shVariant.available) return false;
    if (!cdVariant || !cdVariant.available) return false;

    return true;
  }

  /**
   * Get currently selected options
   */
  function getSelectedOptions() {
    const options = [];

    optionSelects.forEach((select) => {
      options.push(select.value);
    });

    // Also handle radio-style inputs (color swatches)
    const optionGroups = section.querySelectorAll('[data-product-option]');
    optionGroups.forEach((group) => {
      const checkedInput = group.querySelector('[data-product-option-input]:checked');
      if (checkedInput) {
        const index = parseInt(group.dataset.optionIndex, 10);
        options[index] = checkedInput.value;
      }
    });

    return options;
  }

  /**
   * Find variant by selected options
   */
  function findVariant(selectedOptions) {
    return product.variants.find((variant) =>
      variant.options.every((opt, i) => opt === selectedOptions[i])
    );
  }

  function formatMoney(cents) {
    return window.TheColor.utils.formatMoney(cents);
  }

  /**
   * Update product info for selected variant
   */
  function updateVariant(variant) {
    if (!variant) {
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.textContent = 'Unavailable';
      }
      return;
    }

    // Update hidden input
    if (variantIdInput) {
      variantIdInput.value = variant.id;
    }

    // Update H1 to include the selected size (matches the SSR'd Liquid logic).
    if (titleEl && titleBaseText && sizeOptionIndex >= 0) {
      var size = variant['option' + (sizeOptionIndex + 1)];
      titleEl.textContent = size ? (titleBaseText + ' ' + size) : titleBaseText;
    }

    // Update price
    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceEl.className = 'tc-product-main__price-sale';
        if (comparePriceEl) {
          comparePriceEl.textContent = '';
          const s = document.createElement('s');
          s.textContent = formatMoney(variant.compare_at_price);
          comparePriceEl.appendChild(s);
          comparePriceEl.style.display = '';
        }
        if (saveBadgeEl) {
          saveBadgeEl.textContent = 'Save ' + formatMoney(variant.compare_at_price - variant.price);
          saveBadgeEl.style.display = '';
        }
      } else {
        priceEl.className = 'tc-product-main__price-regular';
        if (comparePriceEl) {
          comparePriceEl.style.display = 'none';
        }
        if (saveBadgeEl) {
          saveBadgeEl.style.display = 'none';
        }
      }
    }

    // Update add-to-cart button (for duos, also check component stock)
    var isAvailable = duoComponents ? isDuoVariantAvailable(variant) : variant.available;
    if (addBtn) {
      addBtn.disabled = !isAvailable;
      addBtn.textContent = isAvailable ? 'Add to Cart' : 'Sold Out';
    }

    // Update out-of-stock message and notify form
    var outOfStockEl = section.querySelector('[data-out-of-stock]');
    var notifyFormEl = section.querySelector('[data-notify-form]');
    if (isAvailable) {
      if (outOfStockEl) { outOfStockEl.style.display = 'none'; }
      if (notifyFormEl) { notifyFormEl.style.display = 'none'; }
    } else {
      var stockMsg = (duoComponents && !isDuoVariantAvailable(variant))
        ? 'A component of this duo is currently out of stock'
        : 'This item is currently out of stock';
      if (!outOfStockEl) {
        outOfStockEl = document.createElement('p');
        outOfStockEl.className = 'tc-product-main__out-of-stock';
        outOfStockEl.setAttribute('data-out-of-stock', '');
        outOfStockEl.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg> ' + stockMsg;
        if (addBtn) { addBtn.parentNode.insertBefore(outOfStockEl, addBtn); }
      } else {
        outOfStockEl.lastChild.textContent = ' ' + stockMsg;
      }
      outOfStockEl.style.display = '';
      if (notifyFormEl) { notifyFormEl.style.display = ''; }
    }

    // Update low stock indicator
    var lowStockEl = section.querySelector('[data-low-stock]');
    if (lowStockEl) {
      /* Figures are SSR'd per variant (client product JSON has no
         inventory_quantity) — only toggle the matching span. */
      var anyLow = false;
      lowStockEl.querySelectorAll('[data-low-stock-for]').forEach(function (span) {
        var match = isAvailable && String(variant.id) === span.getAttribute('data-low-stock-for');
        span.hidden = !match;
        if (match) anyLow = true;
      });
      lowStockEl.hidden = !anyLow;
      lowStockEl.style.display = '';
    }

    // Update featured image — falls back to alt/filename match when variant.featured_image is unset
    var resolvedImage = findVariantImage(variant, product);
    if (resolvedImage && mainImage) {
      // Route through the shared gallery switcher so currentImageIndex stays
      // in sync with what the shopper sees (match thumbnail by image id).
      var thumbIdx = -1;
      for (var ti = 0; ti < thumbnails.length; ti++) {
        if (thumbnails[ti].dataset.imageId === String(resolvedImage.id)) {
          thumbIdx = ti;
          break;
        }
      }
      if (thumbIdx !== -1) {
        setMainImage(thumbIdx);
      } else {
        // No matching thumbnail (e.g. single-image product) — update in place
        const baseSrc = resolvedImage.src;
        const src600 = getSizedImageUrl(baseSrc, 600);
        const src900 = getSizedImageUrl(baseSrc, 900);
        const src1200 = getSizedImageUrl(baseSrc, 1200);
        applySrcsetToMain(
          src900,
          src600 + ' 600w, ' + src900 + ' 900w, ' + src1200 + ' 1200w',
          resolvedImage.alt || product.title
        );
      }
    }

    // Update duo hero images when variant size changes
    if (duoComponents && variant) {
      const size = variant.option1;
      const shImg = section.querySelector('[data-duo-sh-image]');
      const cdImg = section.querySelector('[data-duo-cd-image]');
      if (shImg && cdImg) {
        const sh = duoComponents.shampoo;
        const cd = duoComponents.conditioner;
        if (sh && sh.variants) {
          const shV = sh.variants.find(function (v) { return v.option1 === size; });
          const shImage = findVariantImage(shV, sh);
          if (shImage) {
            shImg.src = getSizedImageUrl(shImage.src, 600);
            shImg.srcset = getSizedImageUrl(shImage.src, 400) + ' 400w, ' +
                           getSizedImageUrl(shImage.src, 600) + ' 600w';
            shImg.alt = shImage.alt || sh.title;
          }
        }
        if (cd && cd.variants) {
          const cdV = cd.variants.find(function (v) { return v.option1 === size; });
          const cdImage = findVariantImage(cdV, cd);
          if (cdImage) {
            cdImg.src = getSizedImageUrl(cdImage.src, 600);
            cdImg.srcset = getSizedImageUrl(cdImage.src, 400) + ' 400w, ' +
                           getSizedImageUrl(cdImage.src, 600) + ' 600w';
            cdImg.alt = cdImage.alt || cd.title;
          }
        }
      }
    }

    // Update Freeze & Shine "step up to 1L" nudge — the Liquid SSRs one span
    // per variant with every £/L figure derived from live prices, so JS only
    // toggles the matching span's visibility (same pattern as the
    // [data-unit-price-for] spans). No price is ever typed here: a hardcoded
    // map went stale and overwrote correct SSR unit prices on size change.
    var nudgeEl = section.querySelector('[data-fs-upgrade-nudge]');
    if (nudgeEl) {
      var nudgeId = String(variant.id);
      nudgeEl.querySelectorAll('[data-fs-nudge-for]').forEach(function (el) {
        el.hidden = el.getAttribute('data-fs-nudge-for') !== nudgeId;
      });
    }

    // Update URL without reload
    if (history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    }

    // Dispatch variant:change event for sticky bar and other listeners
    var duoAvail = duoComponents ? isDuoVariantAvailable(variant) : null;
    document.dispatchEvent(new CustomEvent('variant:change', {
      detail: { variant: variant, duoAvailable: duoAvail }
    }));
  }

  /**
   * Handle option changes — debounced via rAF so rapid clicks (or
   * select-and-tab keyboard nav) don't trigger duplicate gallery + price work
   * within the same microtask via Promise.resolve() — independent of
   * requestAnimationFrame, which can be throttled in inactive tabs and
   * was causing the variant update to silently never fire (the rAF
   * callback was being scheduled but never invoked, so chip clicks
   * appeared to "do nothing" until a refresh).
   */
  let optionPending = false;
  function onOptionChange() {
    if (optionPending) return;
    optionPending = true;
    Promise.resolve().then(() => {
      optionPending = false;
      const selectedOptions = getSelectedOptions();
      const variant = findVariant(selectedOptions);
      updateVariant(variant);
    });
  }

  // Delegated listener on the section — handles every variant input
  // (selects, swatches, chips) regardless of render order or DOM rewrites.
  // The previous approach (optionInputs.forEach + addEventListener) was
  // attaching at IIFE init but the listener wasn't firing on the new chip
  // markup; delegation sidesteps the diagnosis and is more robust anyway.
  section.addEventListener('change', (e) => {
    const t = e.target;
    if (t && (t.matches('[data-product-option-input]') || t.matches('[data-product-option-select]'))) {
      onOptionChange();
    }
  });

  /**
   * Thumbnail gallery — routes through the shared setMainImage so the
   * WebP <source> and currentImageIndex stay in sync.
   */
  thumbnails.forEach((thumb, index) => {
    thumb.addEventListener('click', () => {
      setMainImage(index);
    });
  });

  /**
   * Touch swipe for main image (mobile) — same shared setMainImage path as
   * thumbnail clicks and variant changes.
   */
  (function () {
    if (!mainImage || thumbnails.length <= 1) return;

    let startX = 0;

    mainImage.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    mainImage.addEventListener('touchend', (e) => {
      const endX = e.changedTouches[0].clientX;
      const diff = startX - endX;
      const threshold = 50;

      if (Math.abs(diff) < threshold) return;

      if (diff > 0 && currentImageIndex < thumbnails.length - 1) {
        setMainImage(currentImageIndex + 1);
      } else if (diff < 0 && currentImageIndex > 0) {
        setMainImage(currentImageIndex - 1);
      }
    }, { passive: true });
  })();

  /**
   * Image zoom
   */
  if (zoomTrigger && zoomModal && zoomImage) {
    let previousFocus = null;

    function openZoom() {
      if (!mainImage) return;
      previousFocus = document.activeElement;
      const highResSrc = getSizedImageUrl(mainImage.src, 1600);
      zoomImage.src = highResSrc;
      zoomImage.alt = mainImage.alt;
      zoomModal.classList.add('is-open');
      zoomModal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (zoomClose) zoomClose.focus();
    }

    function closeZoom() {
      zoomModal.classList.remove('is-open');
      zoomModal.setAttribute('aria-hidden', 'true');
      zoomImage.src = '';
      document.body.style.overflow = '';
      if (previousFocus) previousFocus.focus();
    }

    zoomTrigger.addEventListener('click', openZoom);
    if (zoomClose) zoomClose.addEventListener('click', closeZoom);
    zoomModal.addEventListener('click', (e) => {
      if (e.target === zoomModal || e.target === zoomImage) closeZoom();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && zoomModal.classList.contains('is-open')) {
        closeZoom();
      }
    });
  }

  /**
   * Quantity controls (with max bound)
   */
  if (qtyInput) {
    if (qtyMinus) {
      qtyMinus.addEventListener('click', () => {
        const val = parseInt(qtyInput.value, 10);
        if (val > 1) {
          qtyInput.value = val - 1;
        }
      });
    }

    if (qtyPlus) {
      qtyPlus.addEventListener('click', () => {
        const val = parseInt(qtyInput.value, 10);
        if (val < MAX_QUANTITY) {
          qtyInput.value = val + 1;
        }
      });
    }

    qtyInput.addEventListener('change', () => {
      let val = parseInt(qtyInput.value, 10);
      if (isNaN(val) || val < 1) {
        val = 1;
      } else if (val > MAX_QUANTITY) {
        val = MAX_QUANTITY;
      }
      qtyInput.value = val;
    });
  }

  /**
   * Back-in-stock notify — the "Notify Me" button sits inside the product
   * <form>, so it can't be its own <form>. POST the email to Shopify's
   * /contact endpoint via fetch (no app needed): the salon gets an email with
   * the product URL, customer address as reply-to. Captures OOS demand (the
   * sold-out 1L hero variant + ~21 other out-of-stock SKUs). The button did
   * nothing before this, so the failure path (graceful message + re-enable) is
   * strictly no worse than the prior dead button.
   */
  const notifyBtn = section.querySelector('[data-notify-submit]');
  if (notifyBtn) {
    notifyBtn.addEventListener('click', () => {
      const emailEl = section.querySelector('[data-notify-email]');
      const msgEl = section.querySelector('[data-notify-msg]');
      const email = emailEl ? emailEl.value.trim() : '';
      if (!email || email.indexOf('@') < 1) {
        if (msgEl) { msgEl.textContent = 'Please enter a valid email address.'; }
        if (emailEl) emailEl.focus();
        return;
      }
      const original = notifyBtn.textContent;
      notifyBtn.disabled = true;
      notifyBtn.textContent = 'Sending…';
      const body = new URLSearchParams();
      body.append('form_type', 'contact');
      body.append('utf8', '✓');
      body.append('contact[email]', email);
      body.append('contact[body]', 'Back-in-stock request: ' + document.title + ' — ' + window.location.href);
      fetch('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      })
        .then((r) => {
          if (!r.ok) throw new Error('status ' + r.status);
          if (msgEl) { msgEl.textContent = "✓ Thanks — we'll email you the moment it's back in stock."; }
          if (emailEl) emailEl.value = '';
          notifyBtn.textContent = 'Done ✓';
        })
        .catch(() => {
          if (msgEl) { msgEl.textContent = 'Sorry, that didn’t send — please email the salon and we’ll let you know.'; }
          notifyBtn.disabled = false;
          notifyBtn.textContent = original;
        });
    });
  }

  /**
   * Add to Cart — delegates to centralised TheColor.Cart API
   */
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();

      if (!addBtn || addBtn.disabled) return;

      const variantId = variantIdInput ? parseInt(variantIdInput.value, 10) : null;
      const quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;

      if (!variantId) return;

      // Disable button and show loading
      addBtn.disabled = true;
      const originalText = addBtn.textContent;
      addBtn.textContent = 'Adding...';

      /* add_to_cart is no longer fired here. It fired BEFORE the add was
         attempted, so sold-out 422s and network failures counted as adds, and it
         covered only this one path. cart.js now emits it from
         CartActions.addToCart — the single funnel every add surface routes
         through — and only once the add has actually succeeded. */

      // Safety guard — fail gracefully if cart module hasn't loaded (e.g. network error)
      if (!window.TheColor || !window.TheColor.Cart) {
        addBtn.disabled = false;
        addBtn.textContent = originalText;
        return;
      }

      // Use centralised cart API
      window.TheColor.Cart.addToCart(variantId, quantity)
        .then(() => {
          addBtn.textContent = 'Added \u2713';
          setTimeout(() => {
            addBtn.disabled = false;
            addBtn.textContent = originalText;
          }, 2000);
        })
        .catch((err) => {
          addBtn.textContent = err.message || 'Error';
          setTimeout(() => {
            addBtn.disabled = false;
            addBtn.textContent = originalText;
          }, 2000);
        });
    });
  }
})();
